/**
 * Wealth Gap Card
 * Displays wealth gap calculation with goal and net worth
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Wallet, Info } from 'lucide-react';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';

const WealthGapCard = ({ wealthGap, setShowWealthGapDetails, setShowWealthGapCalculator, isViewer = false }) => {
  // Calculate age at exit
  const currentYear = new Date().getFullYear();
  const yearsUntilExit = Math.max(0, (wealthGap?.exit_year || currentYear) - currentYear);
  const ageAtExit = (wealthGap?.current_age || 0) + yearsUntilExit;

  // Calculate future net monthly need
  // monthly_cash_need is now the TOTAL (allocation breakdown is tracked separately)
  const totalCurrentMonthlyNeed = wealthGap?.monthly_cash_need || 0;
  const inflationMultiplier = Math.pow(1 + (wealthGap?.annual_inflation_rate || 2) / 100, yearsUntilExit);
  const monthlyNeedAtExit = totalCurrentMonthlyNeed * inflationMultiplier * (1 + (wealthGap?.lifestyle_change_percentage || 0) / 100);
  const outsideIncomeGrowthRate = (wealthGap?.outside_income_annual_increase || 0) / 100;
  const yearsUntilRetirement = Math.max(0, (wealthGap?.retirement_age || wealthGap?.current_age || 0) - (wealthGap?.current_age || 0));
  const futureOutsideIncomeMonthly = (wealthGap?.outside_income_monthly || 0) * Math.pow(1 + outsideIncomeGrowthRate, yearsUntilRetirement);
  const futureNetMonthlyNeed = Math.max(0, monthlyNeedAtExit - futureOutsideIncomeMonthly);

  // Get required sale price data
  const requiredData = wealthGap?.required_sale_price_data || {};
  const hasRequiredData = wealthGap?.wealth_goal_method === 'monthly_needs' && requiredData.total_capital_needed !== undefined;

  // Calculate current net monthly need
  const currentNetMonthlyNeed = Math.max(0, totalCurrentMonthlyNeed - (wealthGap?.outside_income_monthly || 0));

  // Helper function to calculate FV with monthly withdrawals (growing annuity)
  const calculateFVwithPMT = (pv, annualRate, years, monthlyPmt, annualGrowthRate = 0) => {
    if (years === 0) return pv;
    const monthlyRate = annualRate / 12 / 100;
    const monthlyGrowthRate = annualGrowthRate / 12 / 100;
    const n = years * 12; // Total months

    // Calculate FV of the present value
    const fvPV = pv * Math.pow(1 + monthlyRate, n);

    // Calculate FV of growing withdrawals
    if (monthlyPmt === 0) {
      return fvPV;
    }

    // Handle edge case where return rate equals growth rate
    if (Math.abs(monthlyRate - monthlyGrowthRate) < 1e-10) {
      // When r = g: FV = PV(1+r)^n - PMT * n * (1+r)^(n-1)
      const fvPMT = monthlyPmt * n * Math.pow(1 + monthlyRate, n - 1);
      return fvPV - fvPMT;
    }

    // Normal case: Growing annuity formula
    const fvPMT = monthlyPmt * ((Math.pow(1 + monthlyRate, n) - Math.pow(1 + monthlyGrowthRate, n)) / (monthlyRate - monthlyGrowthRate));
    return fvPV - fvPMT;
  };

  // Calculate monthly withdrawal from the selected source
  const monthlyWithdrawal = wealthGap?.monthly_cash_need_from_non_business || 0;
  const nonBusinessSourceCategory = wealthGap?.non_business_source_category || 'liquid_assets';
  const annualInflationRate = wealthGap?.annual_inflation_rate || 2;

  // Business sale proceeds calculations
  const futureBusinessValue = wealthGap?.future_business_value || wealthGap?.desired_sale_price || 0;
  const ownershipPct = (wealthGap?.ownership_percentage || 100) / 100;
  const taxRate = (wealthGap?.tax_rate_on_sale || 42) / 100;
  const grossProceeds = futureBusinessValue * ownershipPct;
  const lumpSumPercent = wealthGap?.lump_sum_percent || 0;
  const lumpSumGross = grossProceeds * (lumpSumPercent / 100);
  const lumpSumAfterTax = lumpSumGross * (1 - taxRate);

  // Lump sum allocation percentages
  const lumpSumAllocLiquid = wealthGap?.lump_sum_allocation_liquid_assets || 0;
  const lumpSumAllocRealEstate = wealthGap?.lump_sum_allocation_real_estate || 0;
  const lumpSumAllocOtherInv = wealthGap?.lump_sum_allocation_other_investments !== undefined
    ? wealthGap.lump_sum_allocation_other_investments
    : 100;

  // Lump sum amounts by asset category
  const lumpSumToLiquid = lumpSumAfterTax * (lumpSumAllocLiquid / 100);
  const lumpSumToRealEstate = lumpSumAfterTax * (lumpSumAllocRealEstate / 100);
  const lumpSumToOtherInv = lumpSumAfterTax * (lumpSumAllocOtherInv / 100);

  // Calculate FV for each asset class (with growing annuity if withdrawal source)
  const liquidAssetsRate = wealthGap?.liquid_assets_return_rate !== undefined ? wealthGap.liquid_assets_return_rate : 3;
  const liquidAssetsFVBase = nonBusinessSourceCategory === 'liquid_assets'
    ? calculateFVwithPMT(wealthGap?.liquid_assets || 0, liquidAssetsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap?.liquid_assets || 0) * Math.pow(1 + liquidAssetsRate / 100, yearsUntilExit);

  const retirementAccountsRate = wealthGap?.retirement_accounts_return_rate !== undefined ? wealthGap.retirement_accounts_return_rate : 7;
  const retirementAccountsFVBase = nonBusinessSourceCategory === 'retirement_accounts'
    ? calculateFVwithPMT(wealthGap?.retirement_accounts || 0, retirementAccountsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap?.retirement_accounts || 0) * Math.pow(1 + retirementAccountsRate / 100, yearsUntilExit);

  const realEstateRate = wealthGap?.real_estate_return_rate !== undefined ? wealthGap.real_estate_return_rate : 4;
  const realEstateFVBase = nonBusinessSourceCategory === 'real_estate'
    ? calculateFVwithPMT(wealthGap?.real_estate_equity || 0, realEstateRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap?.real_estate_equity || 0) * Math.pow(1 + realEstateRate / 100, yearsUntilExit);

  const otherInvestmentsRate = wealthGap?.other_investments_return_rate !== undefined ? wealthGap.other_investments_return_rate : 6;
  const otherInvestmentsFVBase = nonBusinessSourceCategory === 'other_investments'
    ? calculateFVwithPMT(wealthGap?.other_investments || 0, otherInvestmentsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap?.other_investments || 0) * Math.pow(1 + otherInvestmentsRate / 100, yearsUntilExit);

  // Future value at exit (personal assets only, no business proceeds)
  const liquidAssetsFV = liquidAssetsFVBase;
  const retirementAccountsFV = retirementAccountsFVBase;
  const realEstateFV = realEstateFVBase;
  const otherInvestmentsFV = otherInvestmentsFVBase;

  // Calculate future liabilities
  const futureLiabilities = (wealthGap?.total_liabilities || 0) * Math.pow(1 - (wealthGap?.liability_reduction_rate || 0) / 100, yearsUntilExit);

  // Total future net worth at exit (personal assets only, no business proceeds)
  const futureNetWorth = liquidAssetsFV + retirementAccountsFV + realEstateFV + otherInvestmentsFV - futureLiabilities;

  // Calculate wealth gap/surplus using locally calculated futureNetWorth
  const totalCapitalNeeded = requiredData.total_capital_needed || 0;
  const wealthGapAmount = totalCapitalNeeded - futureNetWorth;
  const localWealthSurplus = Math.max(0, -wealthGapAmount); // Positive if surplus
  const localNetGapFromBusiness = Math.max(0, wealthGapAmount); // Positive if gap

  // Calculate single number gap (for single_number method)
  const isSingleNumber = wealthGap?.wealth_goal_method === 'single_number';
  const singleNumberGoal = wealthGap?.wealth_goal_amount || 0;
  // For single number method, use non_business_net_worth field
  const nonBusinessNetWorth = wealthGap?.non_business_net_worth || 0;
  const singleNumberGap = singleNumberGoal - nonBusinessNetWorth;
  const hasSurplus = singleNumberGap < 0; // Negative gap means surplus

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-orange-600 to-red-600 p-4 text-white">
        <div className="flex items-center space-x-3">
          <Wallet size={24} />
          <div>
            <h3 className="text-lg font-bold">Wealth Gap</h3>
            <p className="text-xs text-orange-100">Personal Financial Readiness</p>
          </div>
        </div>
      </div>
      <div className="p-4">
        {wealthGap && wealthGap.calculated_wealth_goal > 0 ? (
          <div className="space-y-3">
            {/* Required Sale Price or Wealth Surplus (if using monthly needs method) */}
            {wealthGap.wealth_goal_method === 'monthly_needs' && hasRequiredData && (
              <div className={`rounded-lg p-2 border-2 ${
                localWealthSurplus > 0
                  ? 'bg-green-100 border-green-300'
                  : 'bg-orange-100 border-orange-300'
              }`}>
                {localWealthSurplus > 0 ? (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-green-900">Wealth Surplus!</p>
                      <p className="text-lg font-bold text-green-700">
                        {formatCurrency(localWealthSurplus)}
                      </p>
                    </div>
                    <p className="text-xs text-green-600">
                      Your personal net worth covers lifestyle needs
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-orange-900">Required from Business Sale</p>
                      <p className="text-lg font-bold text-orange-700">
                          {formatCurrency(localNetGapFromBusiness)}
                      </p>
                    </div>
                    <p className="text-xs text-orange-600">
                      After personal net worth, to sustain lifestyle
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Single Number Goal Gap Display */}
            {isSingleNumber && singleNumberGoal > 0 && (
              <div className={`rounded-lg p-2 border-2 ${
                hasSurplus
                  ? 'bg-green-100 border-green-300'
                  : 'bg-orange-100 border-orange-300'
              }`}>
                {hasSurplus ? (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-green-900">Wealth Surplus!</p>
                      <p className="text-lg font-bold text-green-700">
                        {formatCurrency(Math.abs(singleNumberGap))}
                      </p>
                    </div>
                    <p className="text-xs text-green-600">
                      Your net worth exceeds your wealth goal
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-orange-900">Wealth Gap</p>
                      <p className="text-lg font-bold text-orange-700">
                        {formatCurrency(singleNumberGap)}
                      </p>
                    </div>
                    <p className="text-xs text-orange-600">
                      Additional wealth needed to reach your goal
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Exit Year and Age */}
            <div className="bg-purple-50 rounded-lg p-2 border border-purple-200">
              <p className="text-xs text-gray-700 text-center">
                Exit Year: <span className="font-bold text-purple-800">{wealthGap.exit_year}</span> @ Age <span className="font-bold text-purple-800">{ageAtExit}</span>
              </p>
            </div>

            {/* Data Table - Different layouts for each method */}
            <div className="bg-gray-50 rounded-lg p-2 border border-gray-200 overflow-x-auto">
              {wealthGap.wealth_goal_method === 'monthly_needs' ? (
                /* Three-Column Layout for Monthly Needs */
                <div className="grid grid-cols-12 gap-2 text-xs min-w-[300px]">
                  {/* Header Row */}
                  <div className="col-span-6 font-bold text-gray-900"></div>
                  <div className="col-span-2 font-bold text-gray-900 text-center">Current</div>
                  <div className="col-span-4 font-bold text-gray-900 text-center">At Exit ({wealthGap.exit_year})</div>

                  {/* Net Monthly Needs */}
                  <div className="col-span-12 border-t border-gray-300 mt-0.5 pt-1"></div>
                  <div className="col-span-6 text-gray-700 font-semibold">Net Monthly Needs</div>
                  <div className="col-span-2 text-center font-semibold text-blue-700">{formatCurrency(currentNetMonthlyNeed)}</div>
                  <div className="col-span-4 text-center font-semibold text-blue-700">{formatCurrency(futureNetMonthlyNeed)}</div>

                  {/* Total Capital Needed */}
                  <div className="col-span-12 border-t border-gray-300 mt-0.5 pt-1"></div>
                  <div className="col-span-6 text-gray-700 font-semibold">Total Capital Needed</div>
                  <div className="col-span-2 text-center text-gray-500">—</div>
                  <div className="col-span-4 text-center font-semibold text-indigo-700">{hasRequiredData ? formatCurrency(requiredData.total_capital_needed) : '—'}</div>

                  {/* Net Worth */}
                  <div className="col-span-12 border-t border-gray-300 mt-0.5 pt-1"></div>
                  <div className="col-span-6 text-gray-700 font-semibold">Net Worth</div>
                  <div className="col-span-2 text-center font-semibold text-emerald-700">{formatCurrency(wealthGap.current_net_worth)}</div>
                  <div className="col-span-4 text-center font-semibold text-emerald-700">{hasRequiredData ? formatCurrency(futureNetWorth) : '—'}</div>

                  {/* Gap From Business */}
                  {hasRequiredData && localNetGapFromBusiness > 0 && (
                    <>
                      <div className="col-span-12 border-t border-gray-300 mt-0.5 pt-1"></div>
                      <div className="col-span-6 text-gray-700 font-semibold">Gap From Business</div>
                      <div className="col-span-2 text-center text-gray-500">—</div>
                      <div className="col-span-4 text-center font-semibold text-orange-700">{formatCurrency(localNetGapFromBusiness)}</div>
                    </>
                  )}

                  {/* Estimated Business Exit Value */}
                  <div className="col-span-12 border-t border-gray-300 mt-0.5 pt-1"></div>
                  <div className="col-span-6 text-gray-700 font-semibold">Est. Business Exit Value</div>
                  <div className="col-span-2 text-center font-semibold text-cyan-700">{formatCurrency(wealthGap.desired_sale_price || 0)}</div>
                  <div className="col-span-4 text-center font-semibold text-cyan-700">{formatCurrency(wealthGap.future_business_value || wealthGap.desired_sale_price || 0)}</div>
                </div>
              ) : (
                /* Two-Column Layout for Single Number (Current only) */
                <div className="grid grid-cols-8 gap-2 text-xs">
                  {/* Header Row */}
                  <div className="col-span-5 font-bold text-gray-900"></div>
                  <div className="col-span-3 font-bold text-gray-900 text-center">Current</div>

                  {/* Wealth Goal */}
                  <div className="col-span-8 border-t border-gray-300 mt-0.5 pt-1"></div>
                  <div className="col-span-5 text-gray-700 font-semibold">Wealth Goal</div>
                  <div className="col-span-3 text-center font-semibold text-indigo-700">{formatCurrency(singleNumberGoal)}</div>

                  {/* Non-Business Net Worth */}
                  <div className="col-span-8 border-t border-gray-300 mt-0.5 pt-1"></div>
                  <div className="col-span-5 text-gray-700 font-semibold">Non-Business Net Worth</div>
                  <div className="col-span-3 text-center font-semibold text-emerald-700">{formatCurrency(nonBusinessNetWorth)}</div>

                  {/* Wealth Gap */}
                  {!hasSurplus && singleNumberGap > 0 && (
                    <>
                      <div className="col-span-8 border-t border-gray-300 mt-0.5 pt-1"></div>
                      <div className="col-span-5 text-gray-700 font-semibold">Wealth Gap</div>
                      <div className="col-span-3 text-center font-semibold text-orange-700">{formatCurrency(singleNumberGap)}</div>
                    </>
                  )}

                  {/* Estimated Business Exit Value */}
                  <div className="col-span-8 border-t border-gray-300 mt-0.5 pt-1"></div>
                  <div className="col-span-5 text-gray-700 font-semibold">Est. Business Exit Value</div>
                  <div className="col-span-3 text-center font-semibold text-cyan-700">{formatCurrency(wealthGap.desired_sale_price || 0)}</div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <button
                onClick={() => setShowWealthGapDetails(true)}
                className="flex-1 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 font-semibold transition text-sm flex items-center justify-center gap-1 min-h-[44px]"
              >
                <Info size={16} />
                Details
              </button>
              {!isViewer && (
                <button
                  onClick={() => setShowWealthGapCalculator(true)}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold transition text-sm min-h-[44px]"
                >
                  Update
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-3">Calculate your wealth gap</p>
            {!isViewer && (
              <button
                onClick={() => setShowWealthGapCalculator(true)}
                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold transition text-sm min-h-[44px]"
              >
                Calculate
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

WealthGapCard.propTypes = {
  wealthGap: PropTypes.shape({
    wealth_goal_method: PropTypes.string,
    wealth_goal_amount: PropTypes.number,
    monthly_cash_need: PropTypes.number,
    years_of_income: PropTypes.number,
    annual_inflation_rate: PropTypes.number,
    annual_return_rate: PropTypes.number,
    calculated_wealth_goal: PropTypes.number,
    current_net_worth: PropTypes.number,
    calculated_wealth_gap: PropTypes.number
  }),
  setShowWealthGapDetails: PropTypes.func.isRequired,
  setShowWealthGapCalculator: PropTypes.func.isRequired,
  isViewer: PropTypes.bool
};

export default WealthGapCard;
